import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CategoryPageTemplate from '../components/CategoryPageTemplate';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useProductsByCategory } from '../hooks/useApi';
import apiService from '../services/api';

const CategoryPage = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const subcategoryFilter = searchParams.get('subcategory');
  const navigate = useNavigate();
  const { data: productsData, loading, error } = useProductsByCategory(category);
  const { isUnderMaintenance, isComingSoon } = useSiteSettings();
  const [categoryBanner, setCategoryBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Transform database products to CategoryPageTemplate format
  const subcategoriesData = useMemo(() => {
    if (!productsData?.data?.products) return [];

    const products = productsData.data.products.filter(product => product.isActive !== false);

    // Group products by subcategory
    const groupedBySubcategory = products.reduce((acc, product) => {
      const subcategory = product.subcategory || 'Other';

      if (!acc[subcategory]) {
        acc[subcategory] = [];
      }

      // Transform product to match CategoryPageTemplate expected format
      const transformedProduct = {
        id: product._id,
        name: product.name,
        description: product.description,
        image: product.images?.[0]?.url || '/placeholder.svg',
        features: product.features || []
      };

      acc[subcategory].push(transformedProduct);
      return acc;
    }, {});

    // Convert to array format expected by CategoryPageTemplate
    const allSubcategories = Object.entries(groupedBySubcategory).map(([subcategoryName, products]) => ({
      name: subcategoryName,
      products: products
    }));

    // If a subcategory filter exists in the URL, show only that one
    if (subcategoryFilter) {
      const filtered = allSubcategories.filter(
        s => s.name.toLowerCase() === subcategoryFilter.toLowerCase()
      );
      return filtered.length > 0 ? filtered : allSubcategories;
    }

    return allSubcategories;
  }, [productsData, subcategoryFilter]);

  useEffect(() => {
    const fetchCategoryBanner = async () => {
      setBannerLoading(true);
      try {
        console.log('Fetching category banner for:', category);
        const response = await apiService.getCategoryBanner(category);
        console.log('Category banner API response:', response);
        if (response.status === 'success' && response.data.banner) {
          console.log('Category banner found:', response.data.banner);
          setCategoryBanner(response.data.banner);
        } else {
          console.log('No category banner found, using fallback');
        }
      } catch (error) {
        console.error('Failed to fetch category banner:', error);
      } finally {
        setBannerLoading(false);
      }
    };
    if (category) {
      fetchCategoryBanner();
    }
  }, [category]);

  // Define category-specific content
  const getCategoryContent = (categorySlug) => {
    const categoryMap = {
      'visiting-cards': {
        name: 'Visiting Cards',
        heroTitle: 'Premium Business Card Printing',
        heroSubtitle: 'Professional business cards that make lasting first impressions with premium materials and finishes',
        heroImage: 'https://i.pinimg.com/736x/3e/73/67/3e736712a10e8b84bbb33cc9a058ec5b.jpg'
      },
      'customized-stickers': {
        name: 'Customized Stickers',
        heroTitle: 'Custom Sticker Printing',
        heroSubtitle: 'High-quality custom stickers for branding, labeling, and promotional purposes',
        heroImage: 'https://i.pinimg.com/736x/14/24/93/142493f94faf92c54591ad357b9d1b85.jpg'
      },
      'invitations': {
        name: 'Invitations',
        heroTitle: 'Elegant Invitation Printing',
        heroSubtitle: 'Beautiful invitations and stationery for your special events',
        heroImage: 'https://i.pinimg.com/736x/77/02/fe/7702fe2227f1ff1f8126dc0c38151392.jpg'
      },
      'marketing-essentials': {
        name: 'Marketing Essentials',
        heroTitle: 'Eye-Catching Marketing Materials',
        heroSubtitle: 'Professional marketing materials including posters, flyers, and promotional items to promote your business effectively',
        heroImage: 'https://i.pinimg.com/736x/5d/4e/3f/5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a.jpg'
      },
      'custom-stationary': {
        name: 'Custom Stationery',
        heroTitle: 'Professional Custom Stationery',
        heroSubtitle: 'Personalized stationery for business and personal use',
        heroImage: 'https://i.pinimg.com/736x/a6/c4/71/a6c47161075c44149a3bbcf9aa0d7b45.jpg'
      },
      'office-essentials': {
        name: 'Office Essentials',
        heroTitle: 'Essential Office Printing Solutions',
        heroSubtitle: 'Everything you need for your office printing requirements',
        heroImage: 'https://i.pinimg.com/736x/2b/ba/df/2bbadf47c1487e6b99c05d0329daa39c.jpg'
      },
      // Legacy slugs for backward compatibility
      'business-cards': {
        name: 'Business Cards',
        heroTitle: 'Premium Business Card Printing',
        heroSubtitle: 'Professional business cards that make lasting first impressions with premium materials and finishes',
        heroImage: 'https://i.pinimg.com/736x/3e/73/67/3e736712a10e8b84bbb33cc9a058ec5b.jpg'
      },
      'wedding-invites': {
        name: 'Wedding Invitations',
        heroTitle: 'Elegant Wedding Invitation Printing',
        heroSubtitle: 'Beautiful wedding invitations and stationery for your special day',
        heroImage: 'https://i.pinimg.com/736x/77/02/fe/7702fe2227f1ff1f8126dc0c38151392.jpg'
      },
      'labels-packaging': {
        name: 'Labels & Packaging',
        heroTitle: 'Professional Label & Packaging Solutions',
        heroSubtitle: 'Custom labels and packaging materials for all your business needs',
        heroImage: 'https://i.pinimg.com/736x/c8/9a/1e/c89a1e4b5f2d3c6e7a8b9c0d1e2f3a4b.jpg'
      },
      'posters-marketing': {
        name: 'Posters & Marketing',
        heroTitle: 'Eye-Catching Poster & Marketing Materials',
        heroSubtitle: 'Professional marketing materials to promote your business effectively',
        heroImage: 'https://i.pinimg.com/736x/5d/4e/3f/5d4e3f2a1b0c9d8e7f6a5b4c3d2e1f0a.jpg'
      },
      'books-magazines': {
        name: 'Books & Magazines',
        heroTitle: 'Professional Book & Magazine Printing',
        heroSubtitle: 'High-quality printing solutions for books, magazines, and publications',
        heroImage: 'https://i.pinimg.com/736x/8e/7f/6a/8e7f6a5b4c3d2e1f0a9b8c7d6e5f4a3b.jpg'
      },
      'signage': {
        name: 'Signage',
        heroTitle: 'Professional Signage Solutions',
        heroSubtitle: 'Custom signs and displays for your business',
        heroImage: 'https://i.pinimg.com/736x/1f/0a/9b/1f0a9b8c7d6e5f4a3b2c1d0e9f8a7b6c.jpg'
      },
      'diaries-calendars': {
        name: 'Diaries & Calendars',
        heroTitle: 'Custom Diaries & Calendar Printing',
        heroSubtitle: 'Personalized diaries and calendars for organization and branding',
        heroImage: 'https://i.pinimg.com/736x/9f/8a/7b/9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c.jpg'
      }
    };

    return categoryMap[categorySlug] || {
      name: categorySlug?.replace(/-/g, ' ') || 'Products',
      heroTitle: `${categorySlug?.replace(/-/g, ' ') || 'Product'} Printing Services`,
      heroSubtitle: `Professional printing solutions for ${categorySlug?.replace(/-/g, ' ') || 'your needs'}`,
      heroImage: 'https://i.pinimg.com/736x/2b/ba/df/2bbadf47c1487e6b99c05d0329daa39c.jpg'
    };
  };

  const categoryContent = getCategoryContent(category);

  if (isUnderMaintenance || isComingSoon) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="text-center max-w-2xl">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {isComingSoon ? "Coming Soon" : "Website Under Maintenance"}
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            {isComingSoon
              ? "We're working hard to bring you something amazing. Stay tuned!"
              : "We're currently performing maintenance. Product browsing is temporarily unavailable. You can still view your orders."}
          </p>
          <button
            onClick={() => navigate('/my-orders')}
            className="inline-block bg-[#008000] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#008000] transition-colors"
          >
            View My Orders
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#008000] mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Category Not Found</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link
            to="/"
            className="bg-[#008000] text-white px-6 py-2 rounded-lg hover:bg-[#008000] transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Use banner from API if available, otherwise fallback to hardcoded
  const heroImage = categoryBanner?.image?.url || categoryContent.heroImage;
  const heroMobileImage = categoryBanner?.mobileImage?.url || heroImage;
  const heroTitle = categoryBanner?.headline || categoryContent.heroTitle;
  const heroSubtitle = categoryBanner?.subheadline || categoryContent.heroSubtitle;

  // Return CategoryPageTemplate with transformed data
  return (
    <CategoryPageTemplate
      categoryName={categoryContent.name}
      heroTitle={heroTitle}
      heroSubtitle={heroSubtitle}
      heroImage={heroImage}
      heroMobileImage={heroMobileImage}
      subcategories={subcategoriesData}
    />
  );
};

export default CategoryPage;
